import type { ApplyFormErrorKey, ApplyFormErrors } from "./applyForm";

/**
 * The application, one question at a time.
 *
 * It used to be one long page, on the argument that a wizard hides how long
 * the form is. The counter and the bar answer that here: "Step 1 of 4" says
 * the length up front, and each screen asks for one kind of thing, so a
 * creator on a phone never scrolls past a field they have not reached.
 *
 * `title` and `lede` are keys into the `apply` catalogue, translated where the
 * step renders; the interests lede takes the cap as `{max}`.
 *
 * `fields` is what a step's Continue checks. Everything the server requires
 * belongs to exactly one step, so passing all four is passing the form.
 */
export type ApplyStepId = "about" | "work" | "interests" | "agreement";

export type ApplyStepTitleKey = `steps.${ApplyStepId}.title`;
export type ApplyStepLedeKey = `steps.${ApplyStepId}.lede`;

export type ApplyStep = {
  id: ApplyStepId;
  title: ApplyStepTitleKey;
  lede: ApplyStepLedeKey;
  fields: readonly ApplyFormErrorKey[];
};

export const APPLY_STEPS: readonly ApplyStep[] = [
  {
    id: "about",
    title: "steps.about.title",
    lede: "steps.about.lede",
    fields: ["name", "email", "ageRange", "location"],
  },
  {
    id: "work",
    title: "steps.work.title",
    lede: "steps.work.lede",
    fields: ["portfolioUrl", "tiktokHandle", "instagramHandle"],
  },
  {
    id: "interests",
    title: "steps.interests.title",
    lede: "steps.interests.lede",
    fields: ["interestAreas", "aboutYou"],
  },
  {
    id: "agreement",
    title: "steps.agreement.title",
    lede: "steps.agreement.lede",
    fields: ["agreed", "signatureName"],
  },
];

export function stepHasErrors(
  step: ApplyStep,
  errors: ApplyFormErrors,
): boolean {
  return step.fields.some((field) => errors[field] !== undefined);
}

/**
 * Where to send someone whose final submit still has a problem, or -1.
 *
 * Every Continue checks its own step, so this is a backstop rather than a
 * path anyone is expected to take.
 */
export function firstStepWithErrors(errors: ApplyFormErrors): number {
  return APPLY_STEPS.findIndex((step) => stepHasErrors(step, errors));
}
